import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  User, Mail, Phone, MapPin, Calendar as CalendarIcon, 
  Upload, CreditCard, ChevronLeft, ChevronRight, Shield, Lock, Clock
} from 'lucide-react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutPage = () => {
  const { vehicleId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [vehicle, setVehicle] = useState(null);
  const [locations, setLocations] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [priceDetails, setPriceDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [ageInfo, setAgeInfo] = useState({ age: null, isYoungDriver: false, surcharge: 0 });

  // Booking params
  const pickupLocation = searchParams.get('pickupLocation');
  const initialReturnLocation = searchParams.get('returnLocation');
  const pickupDate = searchParams.get('pickupDate');
  const returnDate = searchParams.get('returnDate');
  const selectedAddOns = searchParams.get('addOns')?.split(',').filter(Boolean) || [];

  // Effective return location (can be overridden by late return)
  const [effectiveReturnLocation, setEffectiveReturnLocation] = useState(initialReturnLocation);
  const [returnLocationLocked, setReturnLocationLocked] = useState(false);
  
  // Agency Meyrin ID for after-hours returns
  const AGENCY_MEYRIN_ID = 'loc_meyrin_agency';

  // Customer form state
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    street: '',
    postalCode: '',
    city: '',
    country: 'Switzerland',
    dateOfBirth: '',
    notes: ''
  });
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Pickup and return times (from URL params or default)
  const [pickupTime, setPickupTime] = useState(searchParams.get('pickupTime') || '10:00');
  const [returnTime, setReturnTime] = useState(searchParams.get('returnTime') || '10:00');

  // Document uploads
  const [idDocument, setIdDocument] = useState(null);
  const [licenseDocument, setLicenseDocument] = useState(null);
  const [idDocumentUrl, setIdDocumentUrl] = useState('');
  const [licenseDocumentUrl, setLicenseDocumentUrl] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch vehicle first, then get vehicle-specific add-ons
        const vehicleRes = await axios.get(`${API_URL}/api/vehicles/${vehicleId}`);
        setVehicle(vehicleRes.data);
        
        const [locRes, addOnsRes] = await Promise.all([
          axios.get(`${API_URL}/api/locations`),
          axios.get(`${API_URL}/api/vehicles/${vehicleId}/addons`)
        ]);
        setLocations(locRes.data);
        // Use vehicle-specific add-ons
        setAddOns(addOnsRes.data.addons || []);

        // Calculate price
        if (pickupDate && returnDate && pickupLocation && effectiveReturnLocation) {
          const priceRes = await axios.post(`${API_URL}/api/calculate-price`, null, {
            params: {
              vehicle_id: vehicleId,
              pickup_date: pickupDate,
              return_date: returnDate,
              pickup_location_id: pickupLocation,
              return_location_id: effectiveReturnLocation,
              add_on_ids: selectedAddOns
            }
          });
          setPriceDetails(priceRes.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load checkout details');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [vehicleId, pickupDate, returnDate, pickupLocation, effectiveReturnLocation]);

  // Handle after-hours return - automatically switch to Agency Meyrin
  useEffect(() => {
    const returnHour = parseInt(returnTime.split(':')[0]);
    const isAfterHours = returnHour >= 20 || returnHour < 6;
    
    if (isAfterHours) {
      // Force return location to Meyrin Agency for after-hours
      setEffectiveReturnLocation(AGENCY_MEYRIN_ID);
      setReturnLocationLocked(true);
    } else {
      // If not after-hours, restore original location and unlock
      setEffectiveReturnLocation(initialReturnLocation);
      setReturnLocationLocked(false);
    }
  }, [returnTime, initialReturnLocation]);

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Check age when date of birth changes
    if (name === 'dateOfBirth' && value) {
      const age = calculateAge(value);
      const isYoungDriver = age !== null && age >= 18 && age < 21;
      const surcharge = isYoungDriver ? 500 : 0;
      setAgeInfo({ age, isYoungDriver, surcharge });
    }
  };

  const handleFileUpload = async (file, type) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', type);

    try {
      const response = await axios.post(`${API_URL}/api/upload/document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.url;
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload document');
      return null;
    }
  };

  const handleIdUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setIdDocument(file);
      const url = await handleFileUpload(file, 'id');
      if (url) setIdDocumentUrl(url);
    }
  };

  const handleLicenseUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setLicenseDocument(file);
      const url = await handleFileUpload(file, 'license');
      if (url) setLicenseDocumentUrl(url);
    }
  };

  const validateForm = () => {
    if (!formData.fullName || !formData.email || !formData.phone || 
        !formData.street || !formData.postalCode || !formData.city || 
        !formData.country || !formData.dateOfBirth) {
      toast.error(t('checkout.fillAllFields', 'Please fill in all required fields'));
      return false;
    }
    
    // Age validation
    const age = calculateAge(formData.dateOfBirth);
    if (age === null) {
      toast.error(t('checkout.invalidDateOfBirth', 'Please enter a valid date of birth'));
      return false;
    }
    if (age < 18) {
      toast.error(t('checkout.minimumAge', 'You must be at least 18 years old to rent a vehicle'));
      return false;
    }
    
    if (!idDocumentUrl || !licenseDocumentUrl) {
      toast.error(t('checkout.uploadDocuments', 'Please upload both ID and driver\'s license'));
      return false;
    }
    
    if (!termsAccepted) {
      toast.error(t('checkout.acceptTerms', 'Please accept the terms and conditions'));
      return false;
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      // Determine if this is an after-hours return
      const returnHour = parseInt(returnTime.split(':')[0]);
      const isAfterHoursReturn = returnHour >= 20 || returnHour < 6;
      
      // Create booking with current language, structured address, and times
      const bookingData = {
        vehicle_id: vehicleId,
        pickup_location_id: pickupLocation,
        return_location_id: effectiveReturnLocation, // Use effective location (may be forced to Meyrin)
        pickup_date: pickupDate,
        pickup_time: pickupTime,
        return_date: returnDate,
        return_time: returnTime,
        add_ons: selectedAddOns,
        customer_info: {
          full_name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          street: formData.street,
          postal_code: formData.postalCode,
          city: formData.city,
          country: formData.country,
          date_of_birth: formData.dateOfBirth,
          id_document_url: idDocumentUrl,
          license_document_url: licenseDocumentUrl
        },
        total_price: priceDetails.total_price,
        notes: formData.notes,
        language: i18n.language, // Pass current language for documents/emails
        // After-hours return info
        return_without_customer: isAfterHoursReturn,
        original_return_location_id: initialReturnLocation // Keep track of original selection
      };

      const bookingRes = await axios.post(`${API_URL}/api/bookings`, bookingData);
      const bookingId = bookingRes.data.booking_id;

      // Create payment session
      const paymentRes = await axios.post(`${API_URL}/api/payments/create-session`, null, {
        params: {
          booking_id: bookingId,
          origin_url: window.location.origin
        }
      });

      // Redirect to Stripe checkout
      const checkoutUrl = paymentRes.data.checkout_url;
      console.log('Redirecting to Stripe:', checkoutUrl);
      
      // Try direct redirect first
      if (checkoutUrl) {
        window.location.assign(checkoutUrl);
      } else {
        throw new Error('No checkout URL received from server');
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      // Show specific error message if available
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create booking. Please try again.';
      toast.error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      setSubmitting(false);
    }
  };

  const getLocationName = (locId) => {
    const loc = locations.find(l => l.id === locId);
    return loc ? (i18n.language === 'fr' ? loc.name_fr : loc.name_en) : '';
  };

  const getAddOnName = (addOnId) => {
    const addOn = addOns.find(a => a.id === addOnId);
    return addOn ? (i18n.language === 'fr' ? addOn.name_fr : addOn.name_en) : '';
  };

  if (loading) {
    return (
      <Layout>
        <div className="section-container py-20 text-center">
          <p className="text-gray-500">{t('common.loading')}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <div className="border-b border-gray-200">
        <div className="section-container py-4">
          <div className="flex items-center gap-2 text-sm">
            <button 
              onClick={() => navigate(-1)}
              className="text-gray-400 hover:text-[#181B1F] transition-colors flex items-center gap-1"
            >
              <ChevronLeft className="w-4 h-4" />
              {t('common.back')}
            </button>
            <ChevronRight className="w-4 h-4 text-gray-300" />
            <span className="text-[#181B1F]">{t('booking.checkout')}</span>
          </div>
        </div>
      </div>

      <div className="section-container py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column - Form */}
          <div className="lg:col-span-2 space-y-8">
            <div
              
              
            >
              <h1 className="font-serif text-4xl text-[#181B1F] font-light mb-2">
                {t('booking.checkout')}
              </h1>
              <p className="text-gray-500">Complete your booking for {vehicle?.name}</p>
            </div>

            {/* Pickup & Return Times */}
            <div
              
              
              
              className="card-feature space-y-6"
              data-testid="pickup-return-times"
            >
              <h3 className="font-serif text-xl text-[#181B1F]">{t('booking.pickupReturnTime', 'Pickup & Return Time')}</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-gray-500">{t('booking.pickupTime', 'Pickup Time')} *</Label>
                  <Select value={pickupTime} onValueChange={setPickupTime}>
                    <SelectTrigger className="input-default" data-testid="pickup-time-select">
                      <Clock className="w-4 h-4 text-gray-400 mr-2" />
                      <SelectValue placeholder="10:00" />
                    </SelectTrigger>
                    <SelectContent className="bg-white shadow-lg border-gray-200 max-h-60">
                      {Array.from({ length: 37 }, (_, i) => {
                        const totalMinutes = 6 * 60 + i * 30;
                        const hours = Math.floor(totalMinutes / 60) % 24;
                        const minutes = totalMinutes % 60;
                        const timeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                        const isLate = hours >= 20 || hours < 6;
                        return (
                          <SelectItem key={timeStr} value={timeStr} className="text-[#181B1F]">
                            {timeStr}{isLate ? ' (+CHF 15)' : ''}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <p className="text-gray-400 text-xs">{pickupDate}</p>
                  {pickupTime && parseInt(pickupTime.split(':')[0]) >= 20 && (
                    <p className="text-yellow-400 text-xs">+CHF 15 late pickup fee applies</p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-500">{t('booking.returnTime', 'Return Time')} *</Label>
                  <Select value={returnTime} onValueChange={setReturnTime}>
                    <SelectTrigger className="input-default" data-testid="return-time-select">
                      <Clock className="w-4 h-4 text-gray-400 mr-2" />
                      <SelectValue placeholder="10:00" />
                    </SelectTrigger>
                    <SelectContent className="bg-white shadow-lg border-gray-200 max-h-60">
                      {Array.from({ length: 37 }, (_, i) => {
                        const totalMinutes = 6 * 60 + i * 30;
                        const hours = Math.floor(totalMinutes / 60) % 24;
                        const minutes = totalMinutes % 60;
                        const timeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                        const isAfterHours = hours >= 20 || hours < 6;
                        return (
                          <SelectItem key={timeStr} value={timeStr} className="text-[#181B1F]">
                            {timeStr}{isAfterHours ? ' (Dropbox)' : ''}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <p className="text-gray-400 text-xs">{returnDate}</p>
                  {returnTime && parseInt(returnTime.split(':')[0]) >= 20 && (
                    <p className="text-gray-500 text-xs">After-hours return: Leave key in agency dropbox</p>
                  )}
                </div>
              </div>

              {/* Pickup & Return Locations */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-200">
                <div className="space-y-2">
                  <Label className="text-gray-500">{t('booking.pickupLocation', 'Pickup Location')}</Label>
                  <div className="input-default flex items-center bg-gray-50">
                    <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-[#181B1F]">{getLocationName(pickupLocation)}</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-500 flex items-center gap-2">
                    {t('booking.returnLocation', 'Return Location')}
                    {returnLocationLocked && (
                      <Lock className="w-3 h-3 text-amber-500" />
                    )}
                  </Label>
                  {returnLocationLocked ? (
                    <div className="input-default flex items-center bg-amber-50 border-amber-200">
                      <MapPin className="w-4 h-4 text-amber-500 mr-2" />
                      <span className="text-[#181B1F]">{getLocationName(effectiveReturnLocation)}</span>
                    </div>
                  ) : (
                    <Select value={effectiveReturnLocation} onValueChange={setEffectiveReturnLocation}>
                      <SelectTrigger className="input-default" data-testid="return-location-select">
                        <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                        <SelectValue placeholder={t('booking.selectLocation', 'Select location')} />
                      </SelectTrigger>
                      <SelectContent className="bg-white shadow-lg border-gray-200">
                        {locations.map((loc) => (
                          <SelectItem key={loc.id} value={loc.id} className="text-[#181B1F]">
                            {i18n.language === 'fr' ? loc.name_fr : loc.name_en}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {returnLocationLocked && (
                    <p className="text-amber-600 text-xs flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      {i18n.language === 'fr' 
                        ? 'Retour tardif: dépôt des clés uniquement à l\'Agence Meyrin'
                        : 'After-hours: key drop-off only at Meyrin Agency'}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Customer Information */}
            <div
              
              
              
              className="card-feature space-y-6"
              data-testid="customer-form"
            >
              <h3 className="font-serif text-xl text-[#181B1F]">{t('booking.customerInfo')}</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName" className="text-gray-500">{t('booking.fullName')} *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className="input-default pl-10"
                      placeholder="John Doe"
                      data-testid="input-fullname"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-500">{t('booking.email')} *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="input-default pl-10"
                      placeholder="john@example.com"
                      data-testid="input-email"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-gray-500">{t('booking.phone')} *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="input-default pl-10"
                      placeholder="+41 22 123 45 67"
                      data-testid="input-phone"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dateOfBirth" className="text-gray-500">{t('booking.dateOfBirth')} *</Label>
                  <div className="relative">
                    <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="dateOfBirth"
                      name="dateOfBirth"
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      className="input-default pl-10"
                      data-testid="input-dob"
                    />
                  </div>
                  {ageInfo.age !== null && ageInfo.age < 18 && (
                    <p className="text-red-400 text-xs" data-testid="age-error">
                      {t('checkout.minimumAge', 'You must be at least 18 years old to rent a vehicle')}
                    </p>
                  )}
                  {ageInfo.isYoungDriver && (
                    <p className="text-yellow-400 text-xs" data-testid="young-driver-warning">
                      {t('checkout.youngDriverSurcharge', 'Drivers aged 18-21: +CHF 500 security deposit surcharge')}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="street" className="text-gray-500">{t('booking.street', 'Street & Number')} *</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="street"
                    name="street"
                    value={formData.street}
                    onChange={handleInputChange}
                    className="input-default pl-10"
                    placeholder="Rue du Mont-Blanc 22"
                    data-testid="input-street"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="postalCode" className="text-gray-500">{t('booking.postalCode', 'Postal Code')} *</Label>
                  <Input
                    id="postalCode"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    className="input-default"
                    placeholder="1201"
                    data-testid="input-postal-code"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city" className="text-gray-500">{t('booking.city', 'City')} *</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="input-default"
                    placeholder="Geneva"
                    data-testid="input-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country" className="text-gray-500">{t('booking.country', 'Country')} *</Label>
                  <Input
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    className="input-default"
                    placeholder="Switzerland"
                    data-testid="input-country"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes" className="text-gray-500">Additional Notes</Label>
                <Textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  className="input-default min-h-[100px]"
                  placeholder="Any special requests or notes..."
                  data-testid="input-notes"
                />
              </div>
            </div>

            {/* Document Upload */}
            <div
              
              
              
              className="card-feature space-y-6"
              data-testid="document-upload"
            >
              <h3 className="font-serif text-xl text-[#181B1F]">{t('booking.documents')}</h3>
              <p className="text-gray-400 text-sm">
                Please upload clear photos of your documents for verification
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* ID Upload */}
                <div className="space-y-3">
                  <Label className="text-gray-500">{t('booking.idDocument')} *</Label>
                  <label 
                    className={`
                      flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-sm cursor-pointer transition-all
                      ${idDocument ? 'border-primary/50 bg-primary/10' : 'border-gray-200 hover:border-white/30'}
                    `}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleIdUpload}
                      className="hidden"
                      data-testid="upload-id"
                    />
                    <Upload className={`w-8 h-8 mb-2 ${idDocument ? 'text-primary' : 'text-gray-400'}`} />
                    <span className={`text-sm ${idDocument ? 'text-primary' : 'text-gray-400'}`}>
                      {idDocument ? idDocument.name : t('booking.uploadDocument')}
                    </span>
                  </label>
                </div>

                {/* License Upload */}
                <div className="space-y-3">
                  <Label className="text-gray-500">{t('booking.licenseDocument')} *</Label>
                  <label 
                    className={`
                      flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-sm cursor-pointer transition-all
                      ${licenseDocument ? 'border-primary/50 bg-primary/10' : 'border-gray-200 hover:border-white/30'}
                    `}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLicenseUpload}
                      className="hidden"
                      data-testid="upload-license"
                    />
                    <Upload className={`w-8 h-8 mb-2 ${licenseDocument ? 'text-primary' : 'text-gray-400'}`} />
                    <span className={`text-sm ${licenseDocument ? 'text-primary' : 'text-gray-400'}`}>
                      {licenseDocument ? licenseDocument.name : t('booking.uploadDocument')}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Payment Info */}
            <div
              
              
              
              className="card-feature space-y-4"
            >
              <div className="flex items-center gap-3">
                <CreditCard className="w-6 h-6 text-primary" />
                <h3 className="font-serif text-xl text-[#181B1F]">{t('booking.payment')}</h3>
              </div>
              <p className="text-gray-400 text-sm">
                {t('booking.paymentDescription')}
              </p>
              <div className="flex items-center gap-4 pt-2">
                <div className="flex items-center gap-2 text-gray-400 text-sm">
                  <Shield className="w-4 h-4" />
                  <span>Secure Payment</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400 text-sm">
                  <Lock className="w-4 h-4" />
                  <span>SSL Encrypted</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Summary */}
          <div className="lg:col-span-1">
            <div className="glass-surface p-6 space-y-6 sticky top-28" data-testid="checkout-summary">
              <h3 className="font-serif text-2xl text-[#181B1F]">{t('booking.summary')}</h3>

              {/* Vehicle */}
              {vehicle && (
                <div className="flex gap-4 pb-4 border-b border-gray-200">
                  <img
                    src={vehicle.images[0] || 'https://images.unsplash.com/photo-1692892529205-9f70ecb1df1d?w=200'}
                    alt={vehicle.name}
                    className="w-24 h-16 object-cover rounded-sm"
                  />
                  <div>
                    <h4 className="text-[#181B1F] font-medium">{vehicle.name}</h4>
                    <p className="text-gray-400 text-sm">{vehicle.brand} • {vehicle.year}</p>
                  </div>
                </div>
              )}

              {/* Rental Details */}
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Pickup</span>
                  <span className="text-[#181B1F] text-right">
                    {pickupDate && format(new Date(pickupDate), 'PP')}<br />
                    <span className="text-gray-500">{getLocationName(pickupLocation)}</span>
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Return</span>
                  <span className="text-[#181B1F] text-right">
                    {returnDate && format(new Date(returnDate), 'PP')}<br />
                    <span className={returnLocationLocked ? "text-amber-600 font-medium" : "text-gray-500"}>
                      {getLocationName(effectiveReturnLocation)}
                      {returnLocationLocked && " (Dropbox)"}
                    </span>
                  </span>
                </div>
              </div>

              {/* Add-ons */}
              {selectedAddOns.length > 0 && (
                <div className="space-y-2 pb-4 border-b border-gray-200">
                  <span className="text-gray-400 text-sm">{t('booking.addOns')}</span>
                  {selectedAddOns.map(addOnId => (
                    <div key={addOnId} className="flex justify-between text-sm">
                      <span className="text-white/70">{getAddOnName(addOnId)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Price Breakdown */}
              {priceDetails && (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-gray-500">
                    <span>{priceDetails.rental_days} {t('booking.days')} × CHF {priceDetails.daily_rate}</span>
                    <span>CHF {(priceDetails.daily_rate * priceDetails.rental_days).toFixed(2)}</span>
                  </div>
                  {priceDetails.add_ons_total > 0 && (
                    <div className="flex justify-between text-gray-500">
                      <span>Add-ons</span>
                      <span>CHF {priceDetails.add_ons_total.toFixed(2)}</span>
                    </div>
                  )}
                  {(priceDetails.pickup_fee > 0 || priceDetails.return_fee > 0) && (
                    <div className="flex justify-between text-gray-500">
                      <span>Delivery fees</span>
                      <span>CHF {(priceDetails.pickup_fee + priceDetails.return_fee).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-[#181B1F] text-lg pt-4 border-t border-gray-200">
                    <span>{t('booking.total')}</span>
                    <span className="font-medium">CHF {priceDetails.total_price.toFixed(2)}</span>
                  </div>
                  
                  {/* Security Deposit Info */}
                  <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                    <div className="flex justify-between text-gray-500 text-xs">
                      <span>{t('booking.securityDeposit', 'Security Deposit (pre-auth)')}</span>
                      <span>CHF {(vehicle?.deposit_amount || 1500).toFixed(2)}</span>
                    </div>
                    {ageInfo.isYoungDriver && (
                      <div className="flex justify-between text-yellow-400 text-xs" data-testid="deposit-surcharge">
                        <span>{t('booking.youngDriverDeposit', 'Young Driver Surcharge (18-21)')}</span>
                        <span>+CHF 500.00</span>
                      </div>
                    )}
                    <div className="flex justify-between text-white/80 text-sm font-medium">
                      <span>{t('booking.totalDeposit', 'Total Deposit')}</span>
                      <span>CHF {((vehicle?.deposit_amount || 1500) + ageInfo.surcharge).toFixed(2)}</span>
                    </div>
                    <p className="text-gray-400 text-xs italic">
                      {t('booking.depositNote', 'Deposit is pre-authorized only, released after vehicle return')}
                    </p>
                  </div>
                </div>
              )}

              {/* Terms and Conditions Checkbox */}
              <div className="flex items-start gap-3 py-4 border-t border-gray-200">
                <Checkbox 
                  id="terms-accepted"
                  checked={termsAccepted}
                  onCheckedChange={setTermsAccepted}
                  className="mt-0.5"
                  data-testid="checkbox-terms"
                />
                <label htmlFor="terms-accepted" className="text-sm text-white/70 leading-relaxed cursor-pointer">
                  {i18n.language === 'fr' ? (
                    <>
                      J'ai lu et j'accepte les{' '}
                      <a href="/terms" target="_blank" className="text-primary hover:underline">
                        conditions générales de location
                      </a>
                    </>
                  ) : (
                    <>
                      I have read and accept the{' '}
                      <a href="/terms" target="_blank" className="text-primary hover:underline">
                        general rental terms and conditions
                      </a>
                    </>
                  )}
                </label>
              </div>

              {/* Submit Button */}
              <Button 
                onClick={handleSubmit}
                disabled={submitting || !termsAccepted}
                className="w-full btn-primary"
                data-testid="confirm-booking-btn"
              >
                {submitting ? t('booking.processing') : t('booking.confirmBooking')}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>

              <p className="text-gray-400 text-xs text-center">
                {i18n.language === 'fr' 
                  ? 'En confirmant, vous acceptez nos conditions générales et notre politique de confidentialité'
                  : 'By confirming, you agree to our terms of service and privacy policy'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CheckoutPage;
